import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }
    fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data: { message?: string; error?: string }) => {
        if (data.message) {
          setStatus("success");
          setMessage(data.message);
          void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        } else {
          setStatus("error");
          setMessage(data.error ?? "Verification failed.");
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Network error. Please try again.");
      });
  }, [queryClient]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center gap-6">
      {status === "loading" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-lg text-slate-600">Verifying your email…</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle className="h-14 w-14 text-emerald-500" />
          <h1 className="text-2xl font-bold text-primary">Email Verified!</h1>
          <p className="text-slate-600 max-w-sm">{message}</p>
          <Button onClick={() => navigate("/")} className="bg-[#1e3a5f] hover:bg-[#16304f]">
            Go to Ayrshire Hub
          </Button>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle className="h-14 w-14 text-red-500" />
          <h1 className="text-2xl font-bold text-primary">Verification Failed</h1>
          <p className="text-slate-600 max-w-sm">{message}</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate("/account")}>
              Go to Account
            </Button>
            <Button onClick={() => navigate("/")} className="bg-[#1e3a5f] hover:bg-[#16304f]">
              Home
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
